import {
  CompositionPlacement,
  CompositionType,
} from '@src/components/sidepanel/CompositionSettings/types';
import { err, ok } from '@src/services/common/Result';
import { formatDate } from '@src/utility/dateFormat';

import type {
  CompositionTemplate,
  ControlConfiguration,
  MediaElement,
} from '@src/components/sidepanel/CompositionSettings/types';
import type { Result } from '@src/services/common/Result';

/**
 * PLAT-11732 — an intro or end card that is just the creator's own image or
 * video, full frame.
 *
 * It has no DatoCMS record. The card is generated from its id, which encodes
 * everything needed to rebuild it, so the id can sit wherever a composition
 * record's id does (`replacedElementIds`, an element's `templateId`) and
 * `getCompositionById` turns it back into a template. Replace, toggling the
 * card back on, and the side panel's controls then work exactly as they do for
 * a designed card.
 */

const ID_PREFIX = 'uploaded-media:';

/** The id of a card's one media element — kept by the saved record. */
const UPLOADED_MEDIA_ELEMENT_ID = 'uploaded-media';

const CENTRED = { positionX: '50%', positionY: '50%' };

/** How long an image card holds when nothing else decides it. */
export const UPLOADED_IMAGE_CARD_DURATION = 4;

export type UploadedMediaCardType = 'introCard' | 'endCard';

export interface UploadedMediaCard {
  cardType: UploadedMediaCardType;
  mediaType: 'image' | 'video';
  url: string;
  /** Seconds. A video card's is the video's own length. */
  duration: number;
}

export function encodeUploadedMediaCardId(card: UploadedMediaCard): string {
  return ID_PREFIX + JSON.stringify(card);
}

export function isUploadedMediaCardId(id: string | undefined): boolean {
  return !!id?.startsWith(ID_PREFIX);
}

/** The card an id encodes, or null for any other id. Never throws. */
export function decodeUploadedMediaCardId(
  id: string | undefined,
): UploadedMediaCard | null {
  if (!id || !isUploadedMediaCardId(id)) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(id.slice(ID_PREFIX.length));
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const { cardType, mediaType, url, duration } = parsed as Record<
    string,
    unknown
  >;
  if (cardType !== 'introCard' && cardType !== 'endCard') return null;
  if (mediaType !== 'image' && mediaType !== 'video') return null;
  if (typeof url !== 'string' || !url) return null;
  if (typeof duration !== 'number' || !(duration > 0)) return null;
  return { cardType, mediaType, url, duration };
}

/**
 * The template for an uploaded media card id, or undefined for any other id.
 *
 * One full-frame element, cover fit, on a fullscreen composition — so it fills
 * every aspect ratio. Its single control swaps the media; for a video it also
 * refits the card to the new video's length (`matchCardDuration`).
 */
export function buildUploadedMediaCardTemplate(
  id: string,
): CompositionTemplate | undefined {
  const card = decodeUploadedMediaCardId(id);
  if (!card) return undefined;

  const isVideo = card.mediaType === 'video';
  const label = isVideo ? 'Uploaded Video' : 'Uploaded Image';
  const control: ControlConfiguration = isVideo
    ? {
        label: 'Video',
        type: 'video',
        elementPaths: [[0]],
        property: 'source',
        matchCardDuration: true,
      }
    : {
        label: 'Image',
        type: 'image',
        elementPaths: [[0]],
        property: 'source',
      };
  const media: MediaElement = {
    id: UPLOADED_MEDIA_ELEMENT_ID,
    type: card.mediaType,
    track: 1,
    source: card.url,
    x: '50%',
    y: '50%',
    width: '100%',
    height: '100%',
    fit: 'cover',
  };
  const isIntro = card.cardType === 'introCard';

  return {
    id,
    label,
    internalName: label,
    aspectRatio: '16:9',
    compositionType: isIntro
      ? CompositionType.INTRO_CARD
      : CompositionType.END_CARD,
    placement: isIntro ? CompositionPlacement.START : CompositionPlacement.END,
    isFullscreen: true,
    overlapBefore: 0,
    overlapAfter: 0,
    controls: [control],
    // Centred in every ratio. The pickers list only designs with a placement
    // for the current ratio, so a saved card needs all three to stay listed.
    sixteenByNine: CENTRED,
    nineBySixteen: CENTRED,
    oneByOne: CENTRED,
    // the side panel's card tile; a video has no still until Dato processes it
    ...(isVideo ? {} : { thumbnail: { url: card.url } }),
    source: { duration: card.duration, elements: [media] },
  };
}

/**
 * The card id for an uploaded file. A video card is sized to the video, so its
 * length is read from the file first; an image card takes the default hold.
 */
export async function resolveUploadedMediaCardId(
  input: Omit<UploadedMediaCard, 'duration'>,
  getMediaDuration: (url: string) => Promise<number>,
): Promise<Result<string>> {
  if (input.mediaType === 'image') {
    return ok(
      encodeUploadedMediaCardId({
        ...input,
        duration: UPLOADED_IMAGE_CARD_DURATION,
      }),
    );
  }

  let duration: number;
  try {
    duration = await getMediaDuration(input.url);
  } catch (error) {
    return err('EXTERNAL', 'Could not read the video length', error);
  }
  if (!Number.isFinite(duration) || duration <= 0) {
    return err('VALIDATION', 'The video reported no usable length');
  }
  return ok(encodeUploadedMediaCardId({ ...input, duration }));
}

/**
 * The card slot a design fills, read off its type OR the timeline edge it
 * lands on — the records are not typed consistently (in dev, intro designs
 * typed `other` but placed at `start`). Null for anything that is not a card.
 */
export function cardSlotOf(
  composition: Pick<CompositionTemplate, 'compositionType' | 'placement'>,
): UploadedMediaCardType | null {
  if (
    composition.compositionType === CompositionType.INTRO_CARD ||
    composition.placement === CompositionPlacement.START
  )
    return 'introCard';
  if (
    composition.compositionType === CompositionType.END_CARD ||
    composition.placement === CompositionPlacement.END
  )
    return 'endCard';
  return null;
}

/**
 * Which uploaded card a group of designs stands for, so a picker can offer an
 * upload beside them — or null when the group is not mostly intro or mostly
 * end cards.
 *
 * Read off the designs, never a category's label (a workspace names those
 * freely), and by majority: in dev the "Intro Cards" category mixes intro
 * cards with transitions, overlays and even a few end cards.
 */
export function uploadedMediaCardTypeFor(
  compositions: Pick<CompositionTemplate, 'compositionType' | 'placement'>[],
): UploadedMediaCardType | null {
  if (!compositions.length) return null;
  let intro = 0;
  let end = 0;
  for (const c of compositions) {
    const slot = cardSlotOf(c);
    if (slot === 'introCard') intro += 1;
    else if (slot === 'endCard') end += 1;
  }
  const majority = compositions.length / 2;
  if (intro > majority) return 'introCard';
  if (end > majority) return 'endCard';
  return null;
}

/**
 * Whether a design is an uploaded media card — by its generated id, or, once
 * saved as a workspace template, by the single full-frame element it was
 * built with (the saved record keeps that element's id).
 */
export function isUploadedMediaCard(
  composition: Pick<CompositionTemplate, 'id' | 'source'> | undefined,
): boolean {
  if (!composition) return false;
  if (isUploadedMediaCardId(composition.id)) return true;
  const elements = composition.source?.elements;
  return (
    Array.isArray(elements) &&
    elements.length === 1 &&
    elements[0]?.id === UPLOADED_MEDIA_ELEMENT_ID
  );
}

/**
 * The template name for an upload: what kind of card it is and the day it was
 * made ("Intro Card · Oct 2, 2026"). Upload file names are camera / CMS ids
 * ("1790972024 0a9a9062") that tell a creator nothing in the grid.
 */
export function uploadedMediaCardName(
  cardType: UploadedMediaCardType,
  date: Date = new Date(),
): string {
  const kind = cardType === 'introCard' ? 'Intro Card' : 'End Card';
  const day = formatDate(date);
  return day ? `${kind} · ${day}` : kind;
}
